/**
 * Conference-scoped Discovery question endpoints + the Inventory endpoints the
 * "Add from Inventory" / "New question" flows read. Everything is per-tenant:
 * super_admin always names the target company (the operate-as-tenant scope), so
 * `companyId` is a required argument here and appended as `?companyId=`.
 *
 * These bypass the Refine dataProvider (verb/shape-specific) and call
 * `superadminFetch` directly, same as `api/conferences.ts`.
 */
import { superadminFetch } from './dataProvider';
import type {
  ConferenceDiscoveryQuestion,
  DiscoveryQuestion,
  QuestionPayload,
} from './discoveryTypes';

export type { QuestionPayload } from './discoveryTypes';

function scope(companyId: string): string {
  return `?companyId=${encodeURIComponent(companyId)}`;
}

// ── Inventory (source templates for "Add from Inventory") ────────────
export async function listInventoryQuestions(companyId: string): Promise<DiscoveryQuestion[]> {
  const { questions } = await superadminFetch<{ questions: DiscoveryQuestion[] }>(
    `/v1/superadmin/questions${scope(companyId)}`,
  );
  return questions;
}

// ── Conference set ──────────────────────────────────────────────────
export async function listConferenceQuestions(
  conferenceId: string,
  companyId: string,
): Promise<ConferenceDiscoveryQuestion[]> {
  const { questions } = await superadminFetch<{ questions: ConferenceDiscoveryQuestion[] }>(
    `/v1/superadmin/conferences/${conferenceId}/questions${scope(companyId)}`,
  );
  return questions;
}

export function createConferenceQuestion(
  conferenceId: string,
  payload: QuestionPayload,
  companyId: string,
): Promise<{ conferenceQuestion: ConferenceDiscoveryQuestion; inventoryQuestion: DiscoveryQuestion }> {
  return superadminFetch(
    `/v1/superadmin/conferences/${conferenceId}/questions${scope(companyId)}`,
    { method: 'POST', body: JSON.stringify(payload) },
  );
}

export async function importInventory(
  conferenceId: string,
  body: { inventoryIds?: string[]; defaults?: true },
  companyId: string,
): Promise<ConferenceDiscoveryQuestion[]> {
  const { questions } = await superadminFetch<{ questions: ConferenceDiscoveryQuestion[] }>(
    `/v1/superadmin/conferences/${conferenceId}/questions/import${scope(companyId)}`,
    { method: 'POST', body: JSON.stringify(body) },
  );
  return questions;
}

export function updateConferenceQuestion(
  conferenceId: string,
  id: string,
  payload: Partial<QuestionPayload>,
  companyId: string,
): Promise<ConferenceDiscoveryQuestion> {
  return superadminFetch<ConferenceDiscoveryQuestion>(
    `/v1/superadmin/conferences/${conferenceId}/questions/${id}${scope(companyId)}`,
    { method: 'PATCH', body: JSON.stringify(payload) },
  );
}

export function reorderConferenceQuestions(
  conferenceId: string,
  orderedIds: string[],
  companyId: string,
): Promise<{ orderedIds: string[] }> {
  return superadminFetch<{ orderedIds: string[] }>(
    `/v1/superadmin/conferences/${conferenceId}/questions/reorder${scope(companyId)}`,
    { method: 'PATCH', body: JSON.stringify({ orderedIds }) },
  );
}

export function deleteConferenceQuestion(
  conferenceId: string,
  id: string,
  companyId: string,
): Promise<void> {
  return superadminFetch<void>(
    `/v1/superadmin/conferences/${conferenceId}/questions/${id}${scope(companyId)}`,
    { method: 'DELETE' },
  );
}
